/**
 * Engine and movement configuration.
 *
 * Every movement coefficient lives here so it can be tuned, compared and
 * recorded alongside replays. Units: metres, seconds, radians, m/s, m/s².
 * See MOVEMENT_SPEC.md for the meaning of each movement parameter.
 */

export interface SimulationConfig {
  /** Fixed simulation rate in Hz. */
  tickRate: number;
  /** Max ticks simulated in one rendered frame before declaring a stall. */
  maxCatchUpTicks: number;
  /** A single frame gap larger than this (ms) is treated as a stall. */
  stallThresholdMs: number;
}

export interface PlayerShapeConfig {
  radius: number;
  /** Total capsule height including both hemispheres. */
  height: number;
  /** Eye height above the capsule's bottom point. */
  eyeHeight: number;
  /** Capsule height and eye height while crouched or sliding (Phase 8). */
  crouchHeight: number;
  crouchEyeHeight: number;
}

export interface MovementConfig {
  gravity: number;
  runSpeed: number;
  /** Exponential rate approaching the input target velocity on ground (1/s). */
  groundAccelRate: number;
  /** Exponential rate approaching rest with no input on ground (1/s). */
  groundDecelRate: number;
  /** Decay rate of speed in excess of runSpeed on ground (1/s). */
  momentumDecayRate: number;
  /** Linear deceleration when input opposes motion above runSpeed (m/s²). */
  brakeDecel: number;
  /** Heading turn rate at zero speed (rad/s). */
  groundTurnRate: number;
  airTurnRate: number;
  /** Speed at which turn rate halves: ω(s) = base / (1 + s / turnSpeedRef). */
  turnSpeedRef: number;
  airControlSpeed: number;
  airAccelRate: number;
  airBrakeDecel: number;
  /**
   * 1 = below airControlSpeed, input that does not oppose the motion never
   * removes planar speed (no chord loss at the regime boundary). 0 = Phase 2.
   * Default 1 since Phase 3B (docs/research/MOMENTUM_MODEL.md).
   */
  airSteerKeepsSpeed: number;
  /**
   * Lead steering in the air (momentum regime). The turn rate is multiplied by
   * airLeadGain while the input leads the velocity by at most airLeadAngleDeg,
   * falling linearly to airLeadSideGain at 90° and beyond. 1 / 1 = Phase 2.
   */
  airLeadGain: number;
  airLeadSideGain: number;
  airLeadAngleDeg: number;
  /** Slope gravity influence multiplier (1 = physical). */
  slopeGain: number;
  maxGroundAngleDeg: number;
  jumpSpeed: number;
  /** 0 = jump along gravity-up, 1 = along ground normal. */
  jumpNormalBlend: number;
  coyoteTicks: number;
  jumpBufferTicks: number;
  /** Ticks after a jump during which ground snapping is suppressed. */
  jumpSnapSuppressTicks: number;
  groundProbeDistance: number;
  /** Max downward step the ground snap may pull the player. */
  snapDistance: number;
  /** Max separation speed from the next ground plane that snapping will absorb. */
  snapMaxSeparationSpeed: number;
  /**
   * Resting contact: the largest per-tick velocity component away from the
   * current ground (m/s) that still counts as staying on it. On a convex
   * curve of radius r this holds the player while v²/r ≤ g·cosθ + tol/dt,
   * so fast players leave crests instead of being glued to them.
   */
  groundSeparationTolerance: number;
  /** Collision skin kept between the capsule and solids. */
  skin: number;
  /**
   * Contact continuity: while sliding along a surface, a hit on a surface whose
   * normal is within this angle (degrees) of the one being slid along carries
   * the sliding velocity across the seam at the same speed (a faceted curve
   * behaves as the curve it approximates). Sharper creases stay inelastic.
   * 0 = off (Phase 2: every seam loses 1 − cos Δθ). Default 10 since Phase 3B.
   */
  contactContinuityDeg: number;
  maxSlideIterations: number;
  maxDepenetrationIterations: number;
  /** Lethal surfaces within this distance of the capsule count as contact. */
  lethalContactDistance: number;
  /** Numerical safety bound (not a gameplay cap). Exceeding it is reported as an error. */
  numericalSpeedLimit: number;

  // Phase 9 direct control (MOVEMENT_SPEC.md, "Phase 9: direct control").
  /**
   * 1 = shooter-style ground control: at or below the top speed, and whenever
   * input is released, friction on the velocity not along the input plus
   * acceleration along it (instant-feeling starts, stops and turns); above the
   * top speed with input, the momentum regime (no cap). The air model is the
   * same either way. 0 = the Phase 2–8 ground model.
   */
  directControl: number;
  /**
   * Ground friction (1/s) on the part of the velocity not along the input (all
   * of it with no input): that part drops by max(its speed, stopSpeed) × friction × dt.
   */
  groundFriction: number;
  /** Below this speed friction acts as if moving at it, so the player comes to a crisp stop. */
  stopSpeed: number;
  /** Ground acceleration: up to groundAccel × wish speed × dt toward the wish velocity each tick. */
  groundAccel: number;

  // Phase 8 techniques (MOVEMENT_SPEC.md, "Techniques").
  /**
   * Air strafing: input may add speed along the wish direction only while the
   * velocity's component along it is below airStrafeSpeed, at up to
   * airStrafeAccel. Turning the view while strafing keeps that component low,
   * so speed builds; holding forward alone adds nothing.
   */
  airStrafeSpeed: number;
  airStrafeAccel: number;
  /** Ticks after landing during which ground friction and steering do not act (a jump in this window keeps all speed); 0 turns it off. */
  bhopGraceTicks: number;
  /** Ground speed while crouched and not sliding. */
  crouchSpeed: number;
  /** Planar speed needed to start a slide. */
  slideMinSpeed: number;
  /** A slide ends (becomes a crouch walk) below this speed. */
  slideEndSpeed: number;
  /** Speed added when a slide starts, at most once per slideBoostCooldownTicks. */
  slideBoost: number;
  slideBoostCooldownTicks: number;
  /** Slide friction: constant deceleration (m/s²) and exponential decay (1/s). */
  slideDecel: number;
  slideDecayRate: number;
  slideTurnRate: number;
  /** Slope gravity multiplier while sliding (no low-speed blend). */
  slideSlopeGain: number;
  /** Distance from the capsule within which a wall counts as touched (wall-run, wall-jump). */
  wallReach: number;
  /** Largest |normal·up| for a surface to count as a wall. */
  wallMaxNormalUp: number;
  wallRunMinSpeed: number;
  wallRunMaxTicks: number;
  /** Gravity multiplier while wall-running. */
  wallRunGravityScale: number;
  wallRunDecayRate: number;
  /** Fastest fall (m/s, downward) at which a wall-run can still begin; slower falls are caught. */
  wallRunMaxEntryFall: number;
  wallJumpOut: number;
  wallJumpUp: number;
  /** A wall-jump pushes off at least restitution × the speed the player hit (or was moving into) the wall with. */
  wallBounceRestitution: number;
  /** Ticks after touching or hitting a wall during which a jump still counts as a wall-jump; 0 turns wall-jumps off. */
  wallCoyoteTicks: number;
  /** Each wall-jump since the ground pushes up by this factor less than the one before (bounded wall-to-wall climbing). */
  wallJumpChainDecay: number;
}

export interface EngineConfig {
  simulation: SimulationConfig;
  player: PlayerShapeConfig;
  movement: MovementConfig;
}

export const DEFAULT_CONFIG: Readonly<EngineConfig> = Object.freeze({
  simulation: Object.freeze({
    tickRate: 120,
    maxCatchUpTicks: 30,
    stallThresholdMs: 250,
  }),
  player: Object.freeze({
    radius: 0.35,
    height: 1.8,
    eyeHeight: 1.62,
    crouchHeight: 1.1,
    crouchEyeHeight: 0.95,
  }),
  movement: Object.freeze({
    gravity: 22,
    runSpeed: 8.5,
    groundAccelRate: 11,
    groundDecelRate: 14,
    momentumDecayRate: 0.35,
    brakeDecel: 24,
    groundTurnRate: 14,
    airTurnRate: 2.6,
    turnSpeedRef: 14,
    airControlSpeed: 5.5,
    airAccelRate: 2.2,
    airBrakeDecel: 5,
    airSteerKeepsSpeed: 1,
    airLeadGain: 1,
    airLeadSideGain: 1,
    airLeadAngleDeg: 25,
    slopeGain: 1,
    maxGroundAngleDeg: 48,
    jumpSpeed: 7.6,
    jumpNormalBlend: 0.25,
    coyoteTicks: 12,
    jumpBufferTicks: 12,
    jumpSnapSuppressTicks: 8,
    groundProbeDistance: 0.06,
    snapDistance: 0.35,
    snapMaxSeparationSpeed: 2.2,
    groundSeparationTolerance: 0.35,
    skin: 0.005,
    contactContinuityDeg: 10,
    maxSlideIterations: 5,
    maxDepenetrationIterations: 4,
    lethalContactDistance: 0.015,
    numericalSpeedLimit: 5000,
    directControl: 1,
    groundFriction: 10,
    stopSpeed: 1,
    groundAccel: 8,
    airStrafeSpeed: 1,
    airStrafeAccel: 40,
    bhopGraceTicks: 4,
    crouchSpeed: 4.2,
    slideMinSpeed: 6.5,
    slideEndSpeed: 4,
    slideBoost: 2.5,
    slideBoostCooldownTicks: 180,
    slideDecel: 2,
    slideDecayRate: 0,
    slideTurnRate: 2,
    slideSlopeGain: 1,
    wallReach: 0.22,
    wallMaxNormalUp: 0.3,
    wallRunMinSpeed: 7,
    wallRunMaxTicks: 180,
    wallRunGravityScale: 0.2,
    wallRunDecayRate: 0.15,
    wallRunMaxEntryFall: 9,
    wallJumpOut: 6.5,
    wallJumpUp: 6.8,
    wallBounceRestitution: 0.8,
    wallCoyoteTicks: 11,
    wallJumpChainDecay: 0.75,
  }),
});

export type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

/** Returns a new config with overrides applied. Never mutates the inputs. */
export function makeConfig(overrides: DeepPartial<EngineConfig> = {}, base: Readonly<EngineConfig> = DEFAULT_CONFIG): EngineConfig {
  const cfg: EngineConfig = {
    simulation: { ...base.simulation, ...overrides.simulation },
    player: { ...base.player, ...overrides.player },
    movement: { ...base.movement, ...overrides.movement },
  };
  validateConfig(cfg);
  return cfg;
}

export function validateConfig(cfg: EngineConfig): void {
  const errors: string[] = [];
  const positive = (name: string, v: number): void => {
    if (!(Number.isFinite(v) && v > 0)) errors.push(`${name} must be a positive finite number (got ${v})`);
  };
  const nonNegative = (name: string, v: number): void => {
    if (!(Number.isFinite(v) && v >= 0)) errors.push(`${name} must be a non-negative finite number (got ${v})`);
  };
  positive('simulation.tickRate', cfg.simulation.tickRate);
  positive('simulation.maxCatchUpTicks', cfg.simulation.maxCatchUpTicks);
  positive('simulation.stallThresholdMs', cfg.simulation.stallThresholdMs);
  positive('player.radius', cfg.player.radius);
  positive('player.height', cfg.player.height);
  if (cfg.player.height < cfg.player.radius * 2) errors.push('player.height must be at least 2 * radius');
  if (!(cfg.player.crouchHeight >= cfg.player.radius * 2 && cfg.player.crouchHeight <= cfg.player.height)) errors.push('player.crouchHeight must be between 2 * radius and player.height');
  if (!(cfg.player.crouchEyeHeight > 0 && cfg.player.crouchEyeHeight <= cfg.player.crouchHeight)) errors.push('player.crouchEyeHeight must be in (0, crouchHeight]');
  if (cfg.movement.slideEndSpeed > cfg.movement.slideMinSpeed) errors.push('movement.slideEndSpeed must not exceed slideMinSpeed');
  if (cfg.movement.wallMaxNormalUp >= 1) errors.push('movement.wallMaxNormalUp must be < 1');
  if (cfg.movement.directControl !== 0 && cfg.movement.directControl !== 1) errors.push('movement.directControl must be 0 or 1');
  if (cfg.movement.wallJumpChainDecay > 1) errors.push('movement.wallJumpChainDecay must be at most 1');
  for (const [k, v] of Object.entries(cfg.movement)) {
    nonNegative(`movement.${k}`, v);
  }
  positive('movement.skin', cfg.movement.skin);
  positive('movement.maxSlideIterations', cfg.movement.maxSlideIterations);
  if (cfg.movement.lethalContactDistance <= cfg.movement.skin) {
    errors.push('movement.lethalContactDistance must exceed movement.skin so resting contact is detected');
  }
  if (cfg.movement.maxGroundAngleDeg >= 90) errors.push('movement.maxGroundAngleDeg must be < 90');
  if (cfg.movement.contactContinuityDeg >= 45) errors.push('movement.contactContinuityDeg must be < 45 (a sharper crease is a corner, not a seam)');
  if (cfg.movement.airLeadAngleDeg <= 0 || cfg.movement.airLeadAngleDeg >= 90) errors.push('movement.airLeadAngleDeg must be in (0, 90)');
  if (!(cfg.movement.airLeadGain > 0) || !(cfg.movement.airLeadSideGain > 0)) errors.push('movement.airLeadGain and airLeadSideGain must be > 0');
  if (errors.length > 0) throw new Error(`Invalid engine config:\n  ${errors.join('\n  ')}`);
}
